import { useMemo, useState } from 'react';
import { useGlobal } from 'reactn';

import {
  AssignmentIcon,
  ForumIcon,
  GroupIcon,
  NotificationsIcon,
} from '@orijinworks/frontend-commons';
import { isPermitted } from '../../../components/permission-control/permission-control';
import { FEED_ANNOUNCEMENT, FEED_ANNOUNCEMENT_VIEW_ALL, FEED_VIEW } from './FeedPermission';
import { canViewFormsTab as canViewFormsTabForUser } from '../../Form/utils/form-permission.util';

/**
 * Feeds-only host gate. Re-Entry Planning is the product home for Forms; the Feeds copy stays
 * behind superadmin until the host anchor is settled, so `form_view` alone is not enough here.
 */
export const canShowFeedsFormsTab = ({ isSuperAdmin } = {}) => Boolean(isSuperAdmin);

export const useFeedConfigureHook = (props) => {
  const {
    unreadCount,
    unreadAnnouncementsCount,
    unreadMessagesCount,
    resetAnnouncementAttachments
  } = props;

  const [global] = useGlobal();
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [modalContext, setModalContext] = useState('');
  const [editSettings, setEditSettings] = useState(null);


  // Event handlers
  const handleNewMessage = (context) => {
    setShowNewMessageModal(true);
    setModalContext(context);
  };

  const handleCloseModal = () => {
    setShowNewMessageModal(false);
    resetAnnouncementAttachments?.();
  };

  const handleOpenEditModal = (settings) => {
    setEditSettings(settings || null);
    setShowNewMessageModal(true);
    setModalContext('edit');
  };

  // Computed values
  const allCount = unreadCount || 0;

  const canViewMessagesTab = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return isPermitted(cbacConfigFlag, permissions, FEED_VIEW, permissionIds);
  }, [global]);

  const canViewAnnouncementsTab = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return (
      isPermitted(cbacConfigFlag, permissions, FEED_ANNOUNCEMENT, permissionIds) ||
      isPermitted(cbacConfigFlag, permissions, FEED_ANNOUNCEMENT_VIEW_ALL, permissionIds)
    );
  }, [global]);

  const canViewFormsTab = useMemo(
    () => canViewFormsTabForUser(global) && canShowFeedsFormsTab(global),
    [global],
  );

  // Tab configuration data
  const tabsConfig = useMemo(() => {
    return [
      {
        key: 'all',
        label: 'All',
        value: 'All',
        Icon: ForumIcon,
        hasUnreadCount: (allCount ?? 0) > 0,
        unreadCount: allCount,
        hide: true,
      },
      {
        key: 'messages',
        label: 'Messages',
        value: 'Messages',
        Icon: GroupIcon,
        hasUnreadCount: (unreadMessagesCount ?? 0) > 0,
        unreadCount: unreadMessagesCount || 0,
        hide: !canViewMessagesTab, // Hide Messages tab when user lacks Feed_View
      },
      {
        key: 'announcements',
        label: 'Announcements',
        value: 'Announcements',
        Icon: NotificationsIcon,
        hasUnreadCount: (unreadAnnouncementsCount ?? 0) > 0,
        unreadCount: unreadAnnouncementsCount ?? 0,
        hide: !canViewAnnouncementsTab,
      },
      {
        Icon: AssignmentIcon,
        key: 'form',
        label: 'Forms',
        value: 'Forms',
        hasUnreadCount: false,
        unreadCount: 0,
        hide: !canViewFormsTab, // Hide Forms tab when user lacks form_view or is not a superadmin
      },
    ]
  }, [allCount, unreadAnnouncementsCount, unreadMessagesCount, canViewMessagesTab, canViewAnnouncementsTab, canViewFormsTab]);

  const onTabChangeHandler = (event, value) => {
    props.onTabChangeHandler && props.onTabChangeHandler(event, value);
  };
  return {
    // State
    showNewMessageModal,
    modalContext,
    editSettings,

    // Computed values
    tabsConfig,
    allCount,

    // Event handlers
    handleNewMessage,
    handleCloseModal,
    handleOpenEditModal,
    onTabChangeHandler,
  };
};